import type { TableColumnConfig } from "echart-table-library";
import { formatCompactLength, formatCount } from "@/shared/utils/format";
import type { TranslateFn } from "@/shared/i18n";
import { PROGRESS_COLORS, type ProgressColumnId } from "./chart-style";

/**
 * Single source for the measure-column block shared by BOTH paper-usage
 * tables (requirement: cùng một bộ cột đo, header, màu fill và định giá trị
 * trên hai bảng). The `printedLength`/`jobCount`/ink-channel fields are
 * business (print metrics) — this stays app-level; only the generic
 * progress rendering lives in the library (`display: "progressColumn"`).
 */

/** Measure columns (everything but the table's id/label column). */
export const MEASURE_COLUMN_IDS = [
  "printedLength",
  "jobCount",
  "cLength",
  "mLength",
  "yLength",
  "kLength",
  "micrLength",
] as const;

export type MeasureColumnId = (typeof MEASURE_COLUMN_IDS)[number];

/** Human header per measure column (English fallback; translatable headers
 *  come from the i18n JSON — see `buildMeasureColumns`). */
export const MEASURE_HEADERS: Record<MeasureColumnId, string> = {
  printedLength: "Printed Length (ft)",
  jobCount: "Job Count",
  cLength: "C(L)",
  mLength: "M(L)",
  yLength: "Y(L)",
  kLength: "K(L)",
  micrLength: "MICR(L)",
};

/** i18n keys for the translatable measure headers (the C(L)-style codes are
 *  language-neutral and keep their fallback labels). */
const MEASURE_HEADER_KEYS: Partial<Record<MeasureColumnId, string>> = {
  printedLength: "charts.axis.printedLengthFt",
  jobCount: "charts.axis.jobCount",
};

/** Value-pill label format per measure column: compact lengths, grouped counts. */
const MEASURE_LABEL_FORMATS: Record<MeasureColumnId, "compact" | "number"> = {
  printedLength: "compact",
  jobCount: "number",
  cLength: "compact",
  mLength: "compact",
  yLength: "compact",
  kLength: "compact",
  micrLength: "compact",
};

// Keep the formatters referenced for consumers that label outside the table
// (e.g. tooltip callbacks) — the library pill formats via `labelFormat`.
export const MEASURE_FORMATTERS: Record<MeasureColumnId, (value: number) => string> = {
  printedLength: formatCompactLength,
  jobCount: formatCount,
  cLength: formatCompactLength,
  mLength: formatCompactLength,
  yLength: formatCompactLength,
  kLength: formatCompactLength,
  micrLength: formatCompactLength,
};

export interface MeasureColumnsOptions {
  /**
   * ORIGINAL (unfiltered) max per measure column — the progress fill scale.
   * Cross-filtered values shrink the bars instead of rescaling them.
   */
  maxima: Record<MeasureColumnId, number>;
  /** Column width in px (default 120). */
  width?: number;
  /** Translate function for the translatable headers (default: i18next). */
  t?: TranslateFn;
}

/**
 * The shared progress measure columns: full-bleed bars (library
 * `progressColumn` display) filled in the chart-matching accent, value on a
 * dark scrim pill, footer total = grouped-digit sums.
 */
export function buildMeasureColumns(options: MeasureColumnsOptions): TableColumnConfig[] {
  const { maxima, width = 120, t } = options;
  return MEASURE_COLUMN_IDS.map((columnId) => ({
    id: columnId,
    header: t
      ? (MEASURE_HEADER_KEYS[columnId] ? t(MEASURE_HEADER_KEYS[columnId]) : MEASURE_HEADERS[columnId])
      : MEASURE_HEADERS[columnId],
    align: "center" as const,
    width,
    display: "progressColumn" as const,
    labelFormat: MEASURE_LABEL_FORMATS[columnId],
    color: PROGRESS_COLORS[columnId as ProgressColumnId],
    columnMax: maxima[columnId],
    // Footer total row: grouped-digit sums of the displayed values.
    aggregate: "sum" as const,
  }));
}
